import { lazy } from "react";
import { Navigate, Route, Routes } from 'react-router-dom';
import AppLayout from "../../components/layout/appLayout";
import NF404 from "../../pages/errorPages/404/404";
import UnauthorizedPage from "../../pages/errorPages/unauthorized/unauthorized.js";
import AnomaliesList from "../../pages/anomalies/anomaliesList.js";
import { AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import LoginPage from "../../pages/login/login.js";
import { CurrentUserProvider, useCurrentUserContext } from "../../context/currentUserContext.js";
import DivisionsList from "../../pages/divisions/divisionsList.js";
import SuppliersList from "../../pages/suppliers/suppliersList.js";
import EditDocument from "../../pages/documents/editDocument.js";
import ViewDocument from "../../pages/documents/documentDetails.js";
import NewDocument from "../../pages/documents/newDocument.js";
import FgasDocumentsList from "../../pages/fgasDocuments/fgasDocumentsList.js";
import { DocumentCategoryProvider } from "../../context/documentCategoryContext.js";
import DistinteDocumentList from "../../pages/distinteDocuments/distinteDocuments.js";
import { documentCategoryCodes, menuLinks, roleCode } from "../../utils/constants.js";
import NewFgasDocument from "../../pages/fgasDocuments/newFgasDocument.js";
import FgasViewDocument from "../../pages/fgasDocuments/fgasDocumentDetails.js";
import FgasEditDocument from "../../pages/fgasDocuments/fgasEditDocument.js";
import NewDistinteDocument from "../../pages/distinteDocuments/newDistinteDocument.js";
import DistinteViewDocument from "../../pages/distinteDocuments/distinteDocumentDetails.js";
import DistinteEditDocument from "../../pages/distinteDocuments/distinteEditDocument.js";
import AttachmentList from "../../pages/supplierAttachments/attashments.js";

// menu access by roles
const documentsMenu = [roleCode.superAdmin, roleCode.admin, roleCode.operator, roleCode.archivio, roleCode.utenteCna];
const anomaliesMenu = [roleCode.superAdmin, roleCode.admin];
const divisionsMenu = [roleCode.superAdmin, roleCode.admin];
const suppliersMenu = [roleCode.superAdmin, roleCode.admin];
const usersMenu = [roleCode.superAdmin, roleCode.admin, roleCode.utenteCna];
const attachmentsMenu = [roleCode.superAdmin, roleCode.admin, roleCode.archivio, roleCode.operator, roleCode.utenteCna];

const ProtectedRoute = ({ children }) => {
    return (
        <>
            <AuthenticatedTemplate>
                {children}
            </AuthenticatedTemplate>

            <UnauthenticatedTemplate>
                <LoginPage />
            </UnauthenticatedTemplate>
        </>
    )
}


const AuthenticatedRoutes = () => {

    const Dashboard = lazy(() => import('../../pages/dashboard/dashboardIndex.js'));
    const Documents = lazy(() => import('../../pages/documents/documentIndex.js'));
    const Users = lazy(() => import('../../pages/users/usersList.js'));

    const RaportiControloRouteGuard = ({ children }) => {
        const { currentUser } = useCurrentUserContext();
        if (currentUser.lastSelectedCategoryCode !== documentCategoryCodes.raportiControllo) {
            return <Navigate to={menuLinks.dashboard} replace />;
        }
        return children;
    };

    const FgasRouteGuard = ({ children }) => {
        const { currentUser } = useCurrentUserContext();
        if (currentUser.lastSelectedCategoryCode !== documentCategoryCodes.fgas) {
            return <Navigate to={menuLinks.dashboard} replace />;
        }
        return children;
    };

    const DistinteRouteGuard = ({ children }) => {
        const { currentUser } = useCurrentUserContext();
        if (currentUser.lastSelectedCategoryCode !== documentCategoryCodes.distinte) {
            return <Navigate to={menuLinks.dashboard} replace />;
        }
        return children;
    };

    const RaportiControloFgasRouteGuard = ({ children }) => {
        const { currentUser } = useCurrentUserContext();
        if (currentUser.lastSelectedCategoryCode === documentCategoryCodes.fgas || currentUser.lastSelectedCategoryCode === documentCategoryCodes.raportiControllo) {
            return children;
        }
        else {
            return <Navigate to={menuLinks.dashboard} replace />;
        }
    };

    const ProtectedByRole = ({ children, roles }) => {
        const { currentUser } = useCurrentUserContext();
        if (currentUser && Object.keys(currentUser).length !== 0) {
            if (currentUser) {
                if (roles && roles.indexOf(currentUser.roleCode) === -1) {
                    // role not authorised so redirect to home page
                    return <Navigate to={{ pathname: '/' }} />
                }
            }
            // has role so return component
            return children;
        }
    };

    return (
        <Routes>

            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/login" element={<LoginPage />} />

            <Route path="/" element={
                <ProtectedRoute>
                    <CurrentUserProvider>
                        <DocumentCategoryProvider>
                            <AppLayout />
                        </DocumentCategoryProvider>
                    </CurrentUserProvider>
                </ProtectedRoute>
            }>

                <Route index path="/" element={<Dashboard />} />

                {/* Dashboard Routes */}
                <Route path={menuLinks.dashboard} element={<Dashboard />} />

                {/* Reporti Controlo Documents Routes */}
                <Route path={menuLinks.raportiControllo} element={<ProtectedByRole roles={documentsMenu}><RaportiControloRouteGuard><Documents /> </RaportiControloRouteGuard></ProtectedByRole>} />
                <Route path={`${menuLinks.raportiControllo + '/id/:id'}`} element={<ProtectedByRole roles={documentsMenu}><RaportiControloRouteGuard><EditDocument /></RaportiControloRouteGuard></ProtectedByRole>} />
                <Route path={`${menuLinks.raportiControllo + '/view/id/:id'}`} element={<ProtectedByRole roles={documentsMenu}><RaportiControloRouteGuard><ViewDocument /></RaportiControloRouteGuard></ProtectedByRole>} />
                <Route path={`${menuLinks.raportiControllo + '/new'}`} element={<ProtectedByRole roles={documentsMenu}><RaportiControloRouteGuard><NewDocument /></RaportiControloRouteGuard></ProtectedByRole>} />

                {/* Fgas Documents Routes */}
                <Route path={menuLinks.fgas} element={<ProtectedByRole roles={documentsMenu}><FgasRouteGuard><FgasDocumentsList /> </FgasRouteGuard></ProtectedByRole>} />
                <Route path={`${menuLinks.fgas + '/new'}`} element={<ProtectedByRole roles={documentsMenu}><FgasRouteGuard><NewFgasDocument /></FgasRouteGuard></ProtectedByRole>} />
                <Route path={`${menuLinks.fgas + '/view/id/:id'}`} element={<ProtectedByRole roles={documentsMenu}><FgasRouteGuard><FgasViewDocument /></FgasRouteGuard></ProtectedByRole>} />
                <Route path={`${menuLinks.fgas + '/id/:id'}`} element={<ProtectedByRole roles={documentsMenu}><FgasRouteGuard><FgasEditDocument /></FgasRouteGuard></ProtectedByRole>} />

                {/* Distinte Document Routes */}
                <Route path={menuLinks.distinte} element={<ProtectedByRole roles={documentsMenu}><DistinteRouteGuard><DistinteDocumentList /></DistinteRouteGuard></ProtectedByRole>} />
                <Route path={`${menuLinks.distinte + '/new'}`} element={<ProtectedByRole roles={documentsMenu}><DistinteRouteGuard><NewDistinteDocument /></DistinteRouteGuard></ProtectedByRole>} />
                <Route path={`${menuLinks.distinte + '/view/id/:id'}`} element={<ProtectedByRole roles={documentsMenu}><DistinteRouteGuard><DistinteViewDocument /></DistinteRouteGuard></ProtectedByRole>} />
                <Route path={`${menuLinks.distinte + '/id/:id'}`} element={<ProtectedByRole roles={documentsMenu}><DistinteRouteGuard><DistinteEditDocument /></DistinteRouteGuard></ProtectedByRole>} />

                {/* Anomalies Routes */}
                <Route path={menuLinks.anomalies} element={<ProtectedByRole roles={anomaliesMenu}><AnomaliesList /></ProtectedByRole>} />

                {/* Divisions Routes */}
                <Route path={menuLinks.divisions} element={<ProtectedByRole roles={divisionsMenu}><DivisionsList /></ProtectedByRole>} />

                {/* Suppliers Routes */}
                <Route path={menuLinks.suppliers} element={<ProtectedByRole roles={suppliersMenu}><SuppliersList /></ProtectedByRole>} />

                {/* Users Routes */}
                <Route path={menuLinks.users} element={<ProtectedByRole roles={usersMenu}><Users /></ProtectedByRole>} />

                {/* Supplier attachments Routes */}
                <Route path={menuLinks.attachments} element={<ProtectedByRole roles={attachmentsMenu}><RaportiControloFgasRouteGuard><AttachmentList /></RaportiControloFgasRouteGuard></ProtectedByRole>} />

                <Route path="*" element={<NF404 />} />
            </Route>

        </Routes>

    )
}

export default AuthenticatedRoutes;